import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { CircularProgress, Container, Grid, Stack, Switch, Typography } from "@mui/material";
import axios from "axios";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import { getDataGridFakeData } from "../../../../helpers/coappDataGridUtils";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectOrganizationId } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";

import { getPluginGroupsColumns, pluginGroupColumns } from "./columns/plugin-groups-columns";

export default function PluginGroups() {
	const dummyRows = getDataGridFakeData(pluginGroupColumns);
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("pluginGroups");
	const { updateCoAppNavigation } = useCoAppNavigation();
	const { pluginId } = useParams();
	const { state } = useLocation();
	const organizationId = useSelector(selectOrganizationId);

	const [pluginActive, setPluginActive] = useState(state?.isActive || false);
	const [pluginGroups, setPluginGroups] = useState(dummyRows);
	const [selectedGroups, setSelectedGroups] = useState([]);
	const [bulkRemoveGroupsDialogOpen, setBulkRemoveGroupsDialogOpen] = useState(false);
	const [confirmGroupRemovalOpen, setConfirmGroupRemovalOpen] = useState(false);
	const [removeGroupDialogOpen, setRemoveGroupDialogOpen] = useState(false);
	const [groupToRemove, setGroupToRemove] = useState({});
	const [groupBeingRemoved, setGroupBeingRemoved] = useState({});
	const [addGroupsOpen, setAddGroupsOpen] = useState(false);
	const [groupsToAdd, setGroupsToAdd] = useState([]);
	const [availableGroups, setAvailableGroups] = useState([]);
	const [loading, setLoading] = useState(true);
	const [optionsLoading, setOptionsLoading] = useState(true);
	
	const handleOpenRemoveGroupConfirmDialog = (group) => {
		setRemoveGroupDialogOpen(true);
		setGroupBeingRemoved(group);
	};

	const columns = useMemo(() => (
		getPluginGroupsColumns(
			loading,
			handleOpenRemoveGroupConfirmDialog
		)
	), [loading]);

	const initPluginGroups = () => {
		axios.get(apiRoutes.getGroupsForPlugin(pluginId), { headers: authHeader })
			.then((response) => {
				setPluginGroups(response.data);
				setLoading(false);
			})
			.catch((error) => {
				console.error("Error fetching plugin groups: ", error);
			});
	};

	const initPluginStatus = () => {
		setPluginActive(state.isActive);
	};

	const handlePluginStatusToggleChange = () => {
		axios.put(apiRoutes.togglePluginStatusForOrganization(organizationId, pluginId), {}, { headers: authHeader })
			.then(() => {
				setPluginActive(!pluginActive);
			})
			.catch((error) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, pluginActive ? messages.PLUGIN_DISABLED_ERROR_MSG : messages.PLUGIN_ENABLED_ERROR_MSG);
			});
	};

	const toggleAddGroupsDialog = () => {
		if (!addGroupsOpen) {
			setAddGroupsOpen(true);
			initAvailableGroups();
		} else {
			setAddGroupsOpen(false);
			setGroupsToAdd([]);
		}
	};

	const initAvailableGroups = () => {
		setOptionsLoading(true);
		axios.get(apiRoutes.getGroups, { headers: authHeader })
			.then((response) => {
				let availableGroups = response.data.filter(group => {
					return !pluginGroups.some(pluginGroup => pluginGroup.id === group.id);
				});
				setAvailableGroups(availableGroups);
				setOptionsLoading(false);
			})
			.catch((error) => {
				console.error("Error fetching available groups: ", error);
			});
	};

	const addGroupsToPlugin = () => {
		if (groupsToAdd.length === 0) return;
		const groupIds = groupsToAdd.map(group => group.id);
		const payload = {
			groups: groupIds
		};
		axios.post(apiRoutes.addGroupsToPlugin(pluginId), payload, { headers: authHeader })
			.then(() => {
				toggleAddGroupsDialog();
				initPluginGroups();
				handleToastAlert(messageLevels.SUCCESS, messages.GROUP_ADDED_SUCCESS_MSG(groupsToAdd.length));
			})
			.catch((error) => {
				console.error("Error adding groups to plugin: ", error);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_ADDED_ERROR_MSG);
			});
	};

	const bulkRemoveGroups = () => {
		if (selectedGroups.length === 0) return;
		const deleteData = {
			groupIds: selectedGroups,
		};
		axios.delete(apiRoutes.bulkRemoveGroupsFromPlugin(pluginId), { headers: authHeader, data: deleteData })
			.then(() => {
				setBulkRemoveGroupsDialogOpen(false);
				initPluginGroups();
				handleToastAlert(messageLevels.INFO, messages.GROUP_REMOVAL_SUCCESS_MSG(selectedGroups.length > 1));
			})
			.catch((err) => {
				console.error(err);
				setBulkRemoveGroupsDialogOpen(false);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_REMOVAL_ERROR_MSG);
			});
	};

	const removePluginGroup = () => {
		axios.delete(apiRoutes.removeGroupFromPlugin(pluginId, groupBeingRemoved.id), { headers: authHeader })
			.then(() => {
				initPluginGroups();
				setRemoveGroupDialogOpen(false);
				handleToastAlert(messageLevels.INFO, messages.GROUP_REMOVAL_SUCCESS_MSG(false));
			})
			.catch((err) => {
				console.error(err);
				setRemoveGroupDialogOpen(false);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_REMOVAL_ERROR_MSG);
			});
	};

	const removeGroupFromPlugin = () => {
		axios.delete(apiRoutes.removeGroupFromPlugin(pluginId, groupToRemove.id), { headers: authHeader })
			.then(() => {
				initPluginGroups();
				setConfirmGroupRemovalOpen(false);
				handleToastAlert(messageLevels.INFO, messages.GROUP_REMOVAL_SUCCESS_MSG(false));
			})
			.catch((err) => {
				console.error(err);
				setConfirmGroupRemovalOpen(false);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_REMOVAL_ERROR_MSG);
			});
	};

	const addGroupsToGroupsToAdd = (value) => {
		value = value.map((groupName) => {
			const foundGroup = availableGroups.find(group => group.name === groupName);
			return foundGroup;
		});
		setGroupsToAdd(value);
	};

	useEffect(() => {
		initPluginStatus();
		initPluginGroups();
		handleInitializeSortModel();
		updateCoAppNavigation(false, navigationItemReferences.pluginNavItemIndex, state.name);
	}, []);

	if (pluginId) {
		return (
			<>
				<CoAppDatagridHeader
					title={`${state.name} Group Management`}
					subResourceType="Group"
					resourceType="Plugin"
					headerSecondaryComponent={
						<Grid container justifyContent={"flex-end"} alignItems={"center"}>
							<Grid item marginRight={1}>
								<Typography variant="body1" color="primary" fontSize="0.875rem" fontWeight={500}>Active</Typography>
							</Grid>
							<Grid item>
								<Switch
									checked={pluginActive}
									onChange={handlePluginStatusToggleChange}
									color="success"
								/>
							</Grid>
						</Grid>
					}
					addIsPresent={true}
					addOnClickHandler={toggleAddGroupsDialog}
					editIsPresent={false}
					deleteIsPresent={false}
				/>
				<CoAppStandardDataGrid
					columns={columns}
					rows={pluginGroups}
					pinnedColumns={["actions", "__check__", "name"]}
					allowSelection={true}
					targetResource="groups"
					parentResource="plugin"
					parentResourceName={state.name}
					bulkActionText="Remove"
					selectionModel={selectedGroups}
					setSelectionModel={setSelectedGroups}
					handleModalToggle={() => setBulkRemoveGroupsDialogOpen(true)}
					loading={loading}
					sortModel={sortModel}
					handleSortModelChange={handleSortModelChange}
				/>
				<CoAppAddItemsToListModal
					addButtonTitle="Save"
					addItemsIsToggled={addGroupsOpen}
					addItemsToCollection={addGroupsToPlugin}
					addItemsToNewArray={addGroupsToGroupsToAdd}
					dialogTitle={`Add group(s) to ${state.name} plugin`}
					inputLabel="Group(s)"
					items={availableGroups}
					itemsToAdd={groupsToAdd}
					toggleDialog={toggleAddGroupsDialog}
					loading={optionsLoading}
				/>
				<CoAppDestructiveConfirmationModal
					dialogOpen={removeGroupDialogOpen}
					dialogTitle={`Remove ${groupBeingRemoved?.name || ""} group from ${state.name} plugin?`}
					dialogMessage={`Are you sure that you want to remove group ${groupBeingRemoved?.name || ""} from the ${state.name} plugin?`}
					confirmClickHandler={removePluginGroup}
					cancelClickHandler={() => setRemoveGroupDialogOpen(false)}
					actionText="Remove"
				/>
				<CoAppDestructiveConfirmationModal
					dialogOpen={confirmGroupRemovalOpen}
					dialogTitle={`Remove ${groupToRemove?.name || ""} group from ${state.name} plugin?`}
					dialogMessage={`Are you sure that you want to remove group ${groupToRemove?.name || ""} from the ${state.name} plugin?`}
					confirmClickHandler={removeGroupFromPlugin}
					cancelClickHandler={() => setConfirmGroupRemovalOpen(false)}
					actionText="Remove"
				/>
				<CoAppDestructiveConfirmationModal
					dialogOpen={bulkRemoveGroupsDialogOpen}
					dialogTitle={`Remove selected ${selectedGroups.length > 1 ? "groups" : "group"} from ${state.name} plugin?`}
					dialogMessage={`Are you sure that you want to remove the selected ${selectedGroups.length > 1 ? "groups" : "group"} from the ${state.name} plugin?`}
					confirmClickHandler={bulkRemoveGroups}
					cancelClickHandler={() => setBulkRemoveGroupsDialogOpen(false)}
					actionText="Remove"
				/>
			</>
		);
	} else {
		return (
			<Container sx={{ maxWidth: "150px!important", marginTop: "50px" }}>
				<Stack spacing={2}>
					<CircularProgress sx={{ color: "#2FBD70" }} />
				</Stack>
			</Container>
		);
	}
}